// src/components/ai/AIChat.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useAI } from '../../hooks/useAI';
import { useAuth } from '../../context/AuthContext';
import AIChatMessage from './AIChatMessage';
import {
    PaperAirplaneIcon, PlusIcon, ChatBubbleLeftRightIcon,
    TrashIcon, SparklesIcon, Bars3Icon,
    MagnifyingGlassIcon, ChevronRightIcon,
    PaperClipIcon, XMarkIcon, DocumentIcon, PhotoIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const QUICK_PROMPTS = [
    { icon: '📊', text: 'Analizza i costi del progetto principale' },
    { icon: '⚠️', text: 'Ci sono rischi di sforamento budget?' },
    { icon: '📈', text: 'Come posso ottimizzare le spese?' },
    { icon: '📋', text: 'Carica un piano PDF per stimare i costi' },
];

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_SIZE_MB = 10;

const AIChat = () => {
    const { user, hasFeature, isSuperAdmin } = useAuth();
    const {
        conversations, currentConversation, messages, aiUsage,
        loading, sending,
        loadConversation, createConversation, sendMessage, sendMessageWithFile, deleteConversation,
    } = useAI();

    const [input, setInput] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState(1);
    const [attachedFile, setAttachedFile] = useState(null);   // { file, preview, name }
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleInput = (e) => {
        setInput(e.target.value);
        setRows(Math.min(e.target.value.split('\n').length, 5));
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast.error('Formato non supportato. Usa JPG, PNG, WEBP o PDF.');
            return;
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            toast.error(`File troppo grande. Massimo ${MAX_SIZE_MB}MB.`);
            return;
        }
        const isImage = file.type.startsWith('image/');
        const preview = isImage ? URL.createObjectURL(file) : null;
        setAttachedFile({ file, preview, name: file.name, type: file.type });
        e.target.value = '';
    };

    const removeAttachment = () => {
        if (attachedFile?.preview) URL.revokeObjectURL(attachedFile.preview);
        setAttachedFile(null);
    };

    const handleSend = async (e) => {
        e?.preventDefault();
        const msg = input.trim();
        if (sending) return;
        if (!msg && !attachedFile) return;

        const fileToSend = attachedFile;
        setInput('');
        setRows(1);
        setAttachedFile(null);

        if (fileToSend) {
            await sendMessageWithFile(fileToSend.file, msg);
        } else {
            await sendMessage(msg);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Quick prompt — crea conversazione e invia atomicamente
    const handleQuickPrompt = async (text) => {
        if (sending) return;
        const result = await createConversation('Nuova chat');
        if (result.success) {
            await sendMessage(text, result.data);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        await deleteConversation(id);
    };

    const aiEnabled = isSuperAdmin || hasFeature('aiEnabled');

    const filtered = conversations.filter(c =>
        c.title?.toLowerCase().includes(search.toLowerCase())
    );

    // ── Gate piano ────────────────────────────────────────────────────────────
    if (!aiEnabled) {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center px-4">
                <div className="w-20 h-20 bg-purple-50 rounded-2xl flex items-center justify-center mb-5">
                    <SparklesIcon className="w-10 h-10 text-purple-300" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">AI Assistant PRO</h2>
                <p className="text-gray-500 text-sm max-w-sm mb-6">
                    L'assistente AI è disponibile nei piani STARTER, PRO ed Enterprise.
                </p>
                <Link to="/settings?tab=plan"
                    className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition font-semibold">
                    <SparklesIcon className="w-4 h-4" /> Vedi piani
                </Link>
            </div>
        );
    }

    // ── Quota badge ───────────────────────────────────────────────────────────
    const quotaLabel = aiUsage
        ? aiUsage.remaining === null
            ? null // illimitato
            : `${aiUsage.used} / ${aiUsage.limit} richieste`
        : null;

    return (
        <div className="flex h-[calc(100vh-130px)] rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">

            {/* ── SIDEBAR ── */}
            <div className={`${sidebarOpen ? 'w-72' : 'w-0'} flex-shrink-0 bg-gray-50 border-r border-gray-100 transition-all duration-300 overflow-hidden flex flex-col`}>
                <div className="p-4 space-y-3">
                    <button onClick={() => createConversation()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition font-medium text-sm">
                        <PlusIcon className="w-4 h-4" /> Nuova chat
                    </button>
                    {conversations.length > 3 && (
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Cerca conversazione..."
                                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent" />
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
                    {filtered.length === 0 ? (
                        <div className="text-center py-10">
                            <ChatBubbleLeftRightIcon className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                            <p className="text-xs text-gray-400">Nessuna conversazione</p>
                        </div>
                    ) : filtered.map(conv => {
                        const active = currentConversation?.id === conv.id;
                        return (
                            <div key={conv.id} onClick={() => loadConversation(conv.id)}
                                className={`group flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition ${active ? 'bg-purple-100 text-purple-900' : 'hover:bg-gray-100 text-gray-700'}`}>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-medium truncate ${active ? 'text-purple-800' : 'text-gray-800'}`}>
                                        {conv.title || 'Conversazione'}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {new Date(conv.updatedAt).toLocaleDateString('it-IT')}
                                    </p>
                                </div>
                                <button onClick={e => handleDelete(conv.id, e)}
                                    className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition rounded-lg hover:bg-red-50">
                                    <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="p-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 px-2 py-1.5">
                        <div className="w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                            {user?.firstName?.[0]}{user?.lastName?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">{user?.firstName} {user?.lastName}</p>
                            {quotaLabel && (
                                <p className="text-xs text-gray-400">{quotaLabel}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MAIN ── */}
            <div className="flex-1 flex flex-col min-w-0">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setSidebarOpen(p => !p)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                            <Bars3Icon className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                                <SparklesIcon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-900">
                                    {currentConversation?.title || 'AI Assistant'}
                                </p>
                                <p className="text-xs text-emerald-500 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Online
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Messaggi */}
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                    {!currentConversation ? (
                        <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto">
                            <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl flex items-center justify-center mb-5">
                                <SparklesIcon className="w-10 h-10 text-purple-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Come posso aiutarti?</h3>
                            <p className="text-gray-500 text-sm mb-8">
                                Sono il tuo assistente AI per la gestione cantieri. Analizzo costi, identifico rischi e suggerisco ottimizzazioni.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                                {QUICK_PROMPTS.map((prompt, i) => (
                                    <button key={i} onClick={() => handleQuickPrompt(prompt.text)}
                                        className="flex items-center gap-3 p-3.5 bg-gray-50 hover:bg-purple-50 border border-gray-100 hover:border-purple-200 rounded-xl text-left transition group">
                                        <span className="text-xl flex-shrink-0">{prompt.icon}</span>
                                        <span className="text-sm text-gray-600 group-hover:text-purple-700">{prompt.text}</span>
                                        <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-purple-400 ml-auto flex-shrink-0" />
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => createConversation()}
                                className="mt-6 flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition font-medium">
                                <PlusIcon className="w-4 h-4" /> Inizia conversazione
                            </button>
                        </div>
                    ) : loading && messages.length === 0 ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, i) => (
                                <AIChatMessage key={msg.id || i} message={msg} />
                            ))}
                            {sending && (
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <SparklesIcon className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                                        <div className="flex gap-1.5 items-center h-4">
                                            {[0, 150, 300].map(delay => (
                                                <span key={delay} className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                                                    style={{ animationDelay: `${delay}ms` }} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Input */}
                {currentConversation && (
                    <div className="px-5 pb-5 pt-3 border-t border-gray-100">
                        {/* Anteprima file allegato */}
                        {attachedFile && (
                            <div className="mb-2 flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2">
                                {attachedFile.preview ? (
                                    <img src={attachedFile.preview} alt="preview"
                                        className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
                                ) : (
                                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <DocumentIcon className="w-5 h-5 text-red-500" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-800 truncate">{attachedFile.name}</p>
                                    <p className="text-xs text-gray-400">
                                        {attachedFile.type === 'application/pdf' ? 'PDF' : 'Immagine'} · {Math.round(attachedFile.file.size / 1024)}KB
                                    </p>
                                </div>
                                <button onClick={removeAttachment}
                                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition">
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleSend}>
                            <div className="flex items-end gap-2 bg-gray-50 rounded-2xl border border-gray-200 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100 transition px-4 py-3">
                                {/* Pulsante upload file */}
                                <button type="button" onClick={() => fileInputRef.current?.click()}
                                    title="Allega PDF o immagine"
                                    className="flex-shrink-0 w-8 h-8 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition flex items-center justify-center">
                                    <PaperClipIcon className="w-4 h-4" />
                                </button>
                                <input ref={fileInputRef} type="file"
                                    accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf"
                                    onChange={handleFileSelect} className="hidden" />

                                <textarea ref={textareaRef} value={input}
                                    onChange={handleInput} onKeyDown={handleKeyDown}
                                    placeholder={attachedFile
                                        ? "Aggiungi una domanda sul file (opzionale)..."
                                        : "Scrivi un messaggio o allega un piano PDF / foto cantiere..."}
                                    disabled={sending} rows={rows}
                                    className="flex-1 bg-transparent resize-none outline-none text-sm text-gray-900 placeholder-gray-400 disabled:opacity-50 max-h-32" />
                                <button type="submit"
                                    disabled={(!input.trim() && !attachedFile) || sending}
                                    className="flex-shrink-0 w-9 h-9 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-30 transition flex items-center justify-center">
                                    <PaperAirplaneIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 text-center mt-2">
                                📎 Allega PDF o immagine · L'AI analizza piani, preventivi e foto cantiere
                            </p>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIChat;